import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { PremiumConfig, PremiumPrice } from 'src/api/internal/schema';
import { useUserContext } from 'src/context/user';
import { PaymentsApi, TrackingApi } from 'src/api';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { intlGetMessagesLocale } from 'src/utils/intl';
import { trackAndSaveCheckoutStarted } from 'src/analytics';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import {
  getSessionStorageEntry,
  removeSessionStorageEntry,
  setSessionStorageEntry,
} from 'src/utils/webStorageUtils';
import { AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { CondensedPlansTrackingLocation } from 'src/api/tracking/types';
import {
  PurchaseTrackingLocation,
  clearCheckoutAttribution,
  rememberCheckoutAttribution,
} from 'src/apps/Payment/pendingPurchase';

const getStorageKeyForPrice = (price: PremiumPrice) =>
  `${price.key}:${price.discount?.key || 'no-discount'}`;

const isPremiumPrice = (value: unknown): value is PremiumPrice =>
  typeof value === 'object' && value !== null && typeof (value as PremiumPrice).key === 'string';

// Phase 2 monetisation funnel: the web purchase flow is a Stripe-checkout redirect, so
// these fire on the canonical plans location. `purchase_completed` is tracked on the
// post-redirect confirmation screen (see Payment/Confirmation), not here.
const DEFAULT_TRACKING_LOCATION: CondensedPlansTrackingLocation = 'condensed_plans_page';

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export type SubscribeHandlerConfig = {
  autoSubscribeToSinglePrice?: boolean;
  productType?: string;
  trackingLocation?: PurchaseTrackingLocation;
  /** Where the upsell was shown; added to purchase events and carried to the confirmation screen. */
  getTrackingSource?: () => string;
  /**
   * When false, checkout still runs but `purchase_initiated` and `purchase_failed`
   * are not sent. Analytics are only for the 2025 experience.
   */
  trackPurchase?: boolean;
};

export function useSubscribeHandler(
  premiumConfig: PremiumConfig | undefined,
  selectedPrice: PremiumPrice | null,
  {
    autoSubscribeToSinglePrice,
    productType,
    trackingLocation = DEFAULT_TRACKING_LOCATION,
    getTrackingSource,
    trackPurchase = true,
  }: SubscribeHandlerConfig = {},
) {
  const intl = useIntl();
  const { safePush, safeReload } = useSafePush();
  const { loading: isLoadingUser, user } = useUserContext();

  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // A successful redirect leaves this true on purpose. The back button restores that
  // frozen page, so clear it or the paywall buttons stay disabled.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsCreatingSession(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const isLoading = isLoadingUser || isWaitingForAuth || isCreatingSession;

  const getSourceTrackingData = useCallback((): Record<string, string> => {
    const source = getTrackingSource?.();
    return source ? { source } : {};
  }, [getTrackingSource]);

  const subscribeWithPrice = useCallback(
    async (price: PremiumPrice) => {
      if (!user || user.isPro) return;

      const sourceTrackingData = getSourceTrackingData();
      if (trackPurchase) {
        void TrackingApi.trackUserAction(
          'purchase_initiated',
          trackingLocation,
          getLanguagePairId(),
          user.isPro,
          { plan_id: price.key, sku: price.key, ...sourceTrackingData },
        );
      }
      rememberCheckoutAttribution({
        planId: price.key,
        trackingLocation,
        source: sourceTrackingData.source,
      });

      setIsCreatingSession(true);
      setError(null);

      const urlParams = new URLSearchParams(window.location.search);
      const nextParam = urlParams.get('next');

      const successParams = new URLSearchParams();
      if (nextParam) {
        successParams.set('next', nextParam);
      }
      if (productType) {
        successParams.set('product_type', productType);
      }
      // Carry the plan id through the Stripe redirect so the confirmation screen can emit
      // `purchase_completed` with `plan_id` once Pro is confirmed (Phase 2 monetisation).
      successParams.set('plan_id', price.key);
      successParams.set('tracking_location', trackingLocation);
      if (sourceTrackingData.source) {
        successParams.set('tracking_source', sourceTrackingData.source);
      }
      const successQuery = successParams.toString();
      const successUrl = `${window.location.origin}${ROUTES.payment.confirmation}${successQuery ? `?${successQuery}` : ''}`;

      const redirectUrl = await PaymentsApi.stripeCreateCheckoutSession({
        price,
        successUrl,
        cancelUrl: window.location.href,
        locale: intlGetMessagesLocale(intl.locale),
        productType,
        customText:
          'Your Pro plan includes: AI conversation practice, Grammar & verb drills, Ad-free experience, All vocab unlocked',
      });

      trackAndSaveCheckoutStarted(user, intl, price);

      await safePush(redirectUrl);
    },
    [safePush, user, intl, productType, trackingLocation, getSourceTrackingData, trackPurchase],
  );

  const subscribeHandler = useCallback(
    (priceOverride?: PremiumPrice) => {
      const price = isPremiumPrice(priceOverride) ? priceOverride : selectedPrice;
      if (!price) {
        return;
      }

      if (!user && !isLoadingUser) {
        setIsWaitingForAuth(true);
        setSessionStorageEntry(
          AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY,
          getStorageKeyForPrice(price),
        );
        return;
      }

      void (async () => {
        try {
          await subscribeWithPrice(price);
        } catch (e) {
          console.error(e);
          clearCheckoutAttribution();
          if (trackPurchase) {
            void TrackingApi.trackUserAction(
              'purchase_failed',
              trackingLocation,
              getLanguagePairId(),
              !!user?.isPro,
              { plan_id: price.key, error: getErrorMessage(e), ...getSourceTrackingData() },
            );
          }
          setError(e);
          // Put the UI back so the learner can retry or continue on Free. Leave the
          // loader up through a successful Stripe redirect.
          setIsCreatingSession(false);
        }
      })();
    },
    [
      user,
      isLoadingUser,
      subscribeWithPrice,
      selectedPrice,
      trackingLocation,
      getSourceTrackingData,
      trackPurchase,
    ],
  );

  const autoSubscribeToPrice = getSessionStorageEntry(AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY);

  useEffect(() => {
    if (!autoSubscribeToPrice || !premiumConfig || !user) return;

    removeSessionStorageEntry(AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY);

    const price = premiumConfig.prices.find(p => getStorageKeyForPrice(p) === autoSubscribeToPrice);

    if (price) {
      void (async () => {
        try {
          await subscribeWithPrice(price);
        } catch (e) {
          clearCheckoutAttribution();
          if (trackPurchase) {
            void TrackingApi.trackUserAction(
              'purchase_failed',
              trackingLocation,
              getLanguagePairId(),
              !!user?.isPro,
              { plan_id: price.key, error: getErrorMessage(e), ...getSourceTrackingData() },
            );
          }
          // if auto-subscribe failed just reload and allow the user to select the plan manually
          await safeReload();
        }
      })();
    }
  }, [
    autoSubscribeToPrice,
    premiumConfig,
    subscribeWithPrice,
    user,
    safeReload,
    trackingLocation,
    getSourceTrackingData,
    trackPurchase,
  ]);

  useEffect(() => {
    if (
      autoSubscribeToSinglePrice &&
      !isLoading &&
      !error &&
      premiumConfig &&
      premiumConfig.prices.length === 1 &&
      selectedPrice
    ) {
      subscribeHandler();
    }
  }, [
    autoSubscribeToSinglePrice,
    isLoading,
    error,
    premiumConfig,
    selectedPrice,
    subscribeHandler,
  ]);

  return {
    isLoading,
    isCreatingSession,
    isWaitingForAuth,
    error,
    subscribeHandler,
  };
}
